import { AgCharts } from 'ag-charts-react';
import { AgChartOptions } from 'ag-charts-community';
import { useMemo } from 'react';
import { formatValueAsString } from '../../../../../utils/helpers';
import { TStoppageEventsItem } from '../../../kpiTypes';
import { ChartContainer } from '../../../../../shared/components/charts/ChartContainer';

interface IChartDataProps {
	data: TStoppageEventsItem[];
}

const StoppageEventsChart: React.FC<IChartDataProps> = ({ ...props }) => {
	const formatOptions = useMemo(() => ({ minimumFractionDigits: 0, maximumFractionDigits: 2 }), []);

	let transformedData = props.data.map((entry) => ({
		startTime: new Date(entry.fromDate).toLocaleDateString('default', {
			day: 'numeric',
			month: 'short',
			year: '2-digit',
		}),
		'Stopped (at anchor)': entry.vesselActivity === 'Stopped (at anchor)' ? entry.timeDays : 0,
		'Stopped (moored)': entry.vesselActivity === 'Stopped (moored)' ? entry.timeDays : 0,
		'Stopped (Unclassified)': entry.vesselActivity === 'Stopped (Unclassified)' ? entry.timeDays : 0,
		generalSeaArea: entry.generalSeaArea,
	}));

	if (!transformedData || transformedData.length === 0) {
		return <div className="w-full h-full flex items-center justify-center">No data available</div>;
	}

	// Filter out entries where all stoppage types are zero
	transformedData = transformedData.filter(
		(item) => item['Stopped (at anchor)'] > 0 || item['Stopped (moored)'] > 0 || item['Stopped (Unclassified)'] > 0,
	);

	const options: AgChartOptions = {
		data: transformedData,
		series: [
			{
				type: 'bar',
				xKey: 'startTime',
				yKey: 'Stopped (at anchor)',
				stacked: true,
				fill: '#00355f',
				tooltip: {
					renderer(params) {
						return {
							data: undefined,
							title: params.datum['generalSeaArea'],
							heading:
								new Date(params.datum['startTime']).toLocaleString('default', {
									day: 'numeric',
									month: 'numeric',
									year: 'numeric',
								}) +
								' : ' +
								formatValueAsString(params.datum['Stopped (at anchor)'], formatOptions, '', '', ''),
						};
					},
				},
			},
			{
				type: 'bar',
				xKey: 'startTime',
				yKey: 'Stopped (moored)',
				stacked: true,
				fill: '#F0D035',
				tooltip: {
					renderer(params: any) {
						return {
							data: undefined,
							title: params.datum['generalSeaArea'],
							heading:
								new Date(params.datum['startTime']).toLocaleString('default', {
									day: 'numeric',
									month: 'numeric',
									year: 'numeric',
								}) +
								' : ' +
								formatValueAsString(params.datum['Stopped (moored)'], formatOptions, '', '', ''),
						};
					},
				},
			},
			{
				type: 'bar',
				xKey: 'startTime',
				yKey: 'Stopped (Unclassified)',
				stacked: true,
				fill: '#819391', // Gray for Unknown
				tooltip: {
					renderer(params) {
						return {
							data: undefined,
							title: params.datum['generalSeaArea'],
							heading:
								new Date(params.datum['startTime']).toLocaleString('default', {
									day: 'numeric',
									month: 'numeric',
									year: 'numeric',
								}) +
								' : ' +
								formatValueAsString(params.datum['Stopped (Unclassified)'], formatOptions, '', '', ''),
						};
					},
				},
			},
		],
		axes: [
			{
				type: 'category',
				position: 'bottom',
				label: {
					autoRotate: true,
					rotation: transformedData.length > 5 ? -90 : 0,
					formatter: (params: any) => {
						return `${new Date(params.value).toLocaleString('default', { day: 'numeric', month: 'short', year: '2-digit' })}`;
					},
				},
			},
			{
				type: 'number',
				position: 'left',
				title: { text: 'Time (Days)' },
				nice: false,
			},
		],
		legend: {
			position: 'top',
			item: {
				marker: {
					shape: 'circle',
				},
			},
		},
	};

	return (
		<ChartContainer data={options.data} filename="stoppage_events_data" buttonPosition="top-right">
			<AgCharts options={options} className="w-full h-full" />
		</ChartContainer>
	);
};

export default StoppageEventsChart;
